import {useState, useMemo, useEffect, createContext} from 'react';
import {
  Conversation,
  ConversationLogItem,
} from '@stripe-internal/conversations-client';

export type DotComChatBubbleAnimationContextValue = {
  animatedMessageId?: string;
  scaleAnimationDisabled: boolean;
  onAnimationFinished: (scaleAnimationDisabled?: boolean) => void;
};
export const defaultContextValue: DotComChatBubbleAnimationContextValue = {
  animatedMessageId: undefined,
  scaleAnimationDisabled: false,
  onAnimationFinished: () => {
    // no-op
  },
};
export const DotComChatBubbleAnimationContext =
  createContext<DotComChatBubbleAnimationContextValue>(defaultContextValue);

/**
 * This is *only* used for the dotcom design to load in sequence the messages based on when their animation finished.
 */
export const useItemsDotCom = (
  conversation: Conversation,
  items: ConversationLogItem[],
) => {
  // Don't initialize on the last item to avoid re-animating it when the chat loads
  const [logIndex, setLogIndex] = useState(items.length);
  const [scaleAnimationDisabled, setScaleAnimationDisabled] = useState(() => {
    // When first time loading the chat, the button options are shown and we disable next message scale animation
    // Selecting an option shows a loading indicator (which has its own scale animation)
    const lastItem = items[items.length - 1];
    return (
      lastItem.name === 'conversation-message-added' &&
      lastItem.message.type === 'button-options-conversation-message'
    );
  });
  const activeItem = items[logIndex];

  const chatBubbleContextValue =
    useMemo((): DotComChatBubbleAnimationContextValue => {
      return {
        animatedMessageId: activeItem?.id,
        scaleAnimationDisabled,
        onAnimationFinished: (scaleDisabled = false) => {
          setLogIndex((prevIndex) => prevIndex + 1);
          setScaleAnimationDisabled(scaleDisabled);
        },
      };
    }, [activeItem?.id, scaleAnimationDisabled]);

  useEffect(() => {
    if (activeItem && activeItem.name === 'conversation-message-added') {
      switch (activeItem.message.type) {
        case 'button-options-conversation-message': {
          // Disable next message scale animation: selecting an option shows a loading indicator (which has its own scale animation)
          chatBubbleContextValue.onAnimationFinished(true);
          break;
        }
        case 'text-conversation-message': {
          const isOwnMessage =
            activeItem.message.participant.id === conversation.user.id;

          if (isOwnMessage) {
            chatBubbleContextValue.onAnimationFinished();
          }
          break;
        }
        case 'inline-data-conversation-message': {
          if (activeItem.message.inlineData.isDone === true) {
            chatBubbleContextValue.onAnimationFinished();
          }
          break;
        }
        default: {
          chatBubbleContextValue.onAnimationFinished();
        }
      }
    }
  }, [activeItem, chatBubbleContextValue, conversation.user.id]);

  useEffect(() => {
    if (activeItem && activeItem.name !== 'conversation-message-added') {
      // If the active item is not a message, we need to reset the log index to avoid hiding new messages
      // e.g conversation ended message
      setLogIndex(items.length);
    }
  }, [items, activeItem]);

  return {
    contextValue: chatBubbleContextValue,
    items: items.slice(0, logIndex + 1),
  };
};
